import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DataTable from "../../components/DataTable";
import Footer from "../../components/Footer";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Swal from "sweetalert2";
import { api, url } from "../../axios/api";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import { numberFormat } from "../../config/Config";
import { Edit, FileText, Plus, Tags, Trash2, Upload } from "lucide-react";

const OPTIONS_TAXES = [
  { value: "NOR_14", label: "NOR (14%)", rawTax: { tipo: "NOR", taxa: 14 } },
  { value: "ISE_0", label: "ISE (0%)", rawTax: { tipo: "ISE", taxa: 0 } },
  { value: "OUT_0", label: "OUT (0%)", rawTax: { tipo: "OUT", taxa: 0 } },
];

const parseTaxes = (taxes) => {
  if (!taxes) return { tipo: "NOR", taxa: 14 };
  if (typeof taxes === "string") {
    try {
      return JSON.parse(taxes);
    } catch {
      return { tipo: "NOR", taxa: 14 };
    }
  }
  return taxes;
};

const getTaxRate = (taxes) => parseFloat(parseTaxes(taxes)?.taxa || 0);
const getFinalPrice = (produto) => (parseFloat(produto?.preco || 0) * (1 + getTaxRate(produto?.taxes) / 100));
const getProductProfit = (produto) => (parseFloat(produto?.preco || 0) - parseFloat(produto?.preco_compra || 0));
const getProductMargin = (produto) => {
  const preco = parseFloat(produto?.preco || 0);
  if (preco <= 0) return 0;
  return (getProductProfit(produto) / preco) * 100;
};

const Produtos = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;
  const importInputRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [produtoData, setProdutoData] = useState({});
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (empresaId) fetchProdutos();
  }, [empresaId]);

  const fetchProdutos = async () => {
    try {
      const response = await api.get(`/app/produtos/filter/?empresa_id=${empresaId}`);
      setData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectRow = (event) => {
    const tr = event.target.closest("tr");
    if (!tr || tr.parentElement.tagName !== "TBODY") return;

    const id = parseInt(tr.cells[0]?.innerText, 10);
    if (Number.isNaN(id)) return;

    setSelectedId(id);
    tr.parentElement.querySelectorAll("tr").forEach((row) => row.classList.remove("win10-row-selected"));
    tr.classList.add("win10-row-selected");
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setProdutoData({
      tipo: "Produto",
      unidade: "un",
      preco: "",
      preco_compra: 0,
      quantidade: 0,
      preco_venda_atendimento: false,
      taxes: { tipo: "NOR", taxa: 14 },
    });
    setShowModal(true);
  };

  const handleOpenEditModal = () => {
    if (!selectedId) return Swal.fire("Aviso", "Selecione um item primeiro na tabela.", "info");
    const item = data.find((produto) => produto.id === selectedId);
    if (!item) return;

    setIsEditing(true);
    setProdutoData({
      ...item,
      preco_venda_atendimento: !!item.preco_venda_atendimento,
      taxes: parseTaxes(item.taxes),
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!selectedId) return;

    const result = await Swal.fire({
      title: "Confirmar",
      text: "Deseja realmente eliminar este item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d13438",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/app/produtos/${selectedId}`);
      setData((current) => current.filter((produto) => produto.id !== selectedId));
      setSelectedId(null);
      Swal.fire("Sucesso", "Registo eliminado com sucesso.", "success");
    } catch (error) {
      Swal.fire("Erro", "Falha ao processar a eliminacao.", "error");
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!produtoData.taxes || Object.keys(produtoData.taxes).length === 0) {
      return Swal.fire("Atencao", "Selecione uma taxa valida.", "warning");
    }

    try {
      const payload = { ...produtoData, empresa_id: empresaId };
      if (isEditing) await api.put(`/app/produtos/${produtoData.id}`, payload);
      else await api.post("/app/produtos", payload);

      await fetchProdutos();
      setShowModal(false);
      Swal.fire("Sucesso", "Dados guardados com sucesso.", "success");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Falha ao guardar o produto.", "error");
    }
  };

  const handleImportProdutos = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post(`/app/produtos/import/${empresaId}/`, formData);
      await fetchProdutos();
      Swal.fire("Importado", response.data.message || "Produtos importados com sucesso.", "success");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Falha ao importar produtos.", "error");
    } finally {
      event.target.value = "";
    }
  };

  const openGeneratedPdf = async (endpoint) => {
    try {
      const response = await api.get(endpoint);
      const pdfPath = response.data?.data?.relativePath;
      if (!pdfPath) throw new Error("PDF nao retornado pelo servidor.");
      window.open(`${url}${pdfPath}`, "_blank", "noopener,noreferrer");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || error.message || "Falha ao gerar PDF.", "error");
    }
  };

  const taxaDefinida = getTaxRate(produtoData.taxes);
  const valorPrecoBase = parseFloat(produtoData.preco) || 0;
  const precoComTaxa = valorPrecoBase * (1 + taxaDefinida / 100);
  const currentTaxValue = `${parseTaxes(produtoData.taxes).tipo}_${parseTaxes(produtoData.taxes).taxa}`;
  const selectedTaxOption = OPTIONS_TAXES.find((option) => option.value === currentTaxValue) || null;

  const selectStyle = {
    control: (base) => ({ ...base, borderRadius: 0, minHeight: "31px", fontSize: "13px", borderColor: "#ced4da" }),
  };

  return (
    <div id="layout-wrapper">
      <Navbar />
      <Sidebar />
      <style>{`
        .win10-card { background: #fff; border: 1px solid #e1e1e1; border-radius: 0; }
        .win10-header { border-bottom: 1px solid #eee; padding: 12px 20px; background: #fff; gap: 8px; }
        .win10-row-selected { background-color: #0078d4 !important; color: #fff !important; }
        .win10-btn { border-radius: 0; font-size: 12px; padding: 6px 12px; border: 1px solid transparent; white-space: nowrap; }
        .win10-btn-primary { background: #0078d4; color: #fff; }
        .win10-btn-outline { background: #fff; border: 1px solid #8a8886; color: #323130; }
        .win10-btn-outline:hover { background: #f3f2f1; }
      `}</style>

      <main className="app-wrapper">
        <div className="container-fluid px-0 mt-0">
          <div className="win10-card shadow-sm">
            <div className="win10-header d-flex justify-content-between align-items-center flex-wrap">
              <span className="text-muted small fw-bold text-uppercase">Gestao de produtos e estoque</span>
              <div className="d-flex gap-1 flex-wrap">
                <input ref={importInputRef} type="file" accept=".csv,.json,.sql" hidden onChange={handleImportProdutos} />
                <button className="win10-btn win10-btn-primary d-flex align-items-center gap-2" onClick={handleOpenAddModal}><Plus size={14} /> Novo</button>
                <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={handleOpenEditModal}><Edit size={14} /> Editar</button>
                <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={() => importInputRef.current?.click()}><Upload size={14} /> Importar</button>
                <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={() => openGeneratedPdf(`/prints/reports/produtos/${empresaId}/`)}><FileText size={14} /> Lista A4</button>
                <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={() => openGeneratedPdf(`/prints/reports/produtos-etiquetas-a4/${empresaId}/`)}><Tags size={14} /> Etiquetas A4</button>
                <button className="win10-btn win10-btn-outline text-danger d-flex align-items-center gap-2" onClick={handleDelete}><Trash2 size={14} /> Eliminar</button>
              </div>
            </div>

            <div className="card-body p-0" onClick={handleSelectRow}>
              <div className="p-3">
                <DataTable
                  columns={[
                    { title: "ID", data: "id" },
                    { title: "DESIGNACAO", data: "nome" },
                    { title: "TIPO", data: "tipo" },
                    { title: "P. COMPRA", data: "preco_compra", render: (value) => `${numberFormat(value, 2)} Kz` },
                    { title: "P. BASE", data: "preco", render: (value) => `${numberFormat(value, 2)} Kz` },
                    { title: "TAX", data: "taxes", render: (value) => `${getTaxRate(value)}%` },
                    { title: "P. FINAL", data: null, render: (value, type, row) => `${numberFormat(getFinalPrice(row), 2)} Kz` },
                    {
                      title: "LUCRO",
                      data: null,
                      render: (value, type, row) => {
                        const lucro = getProductProfit(row);
                        const className = lucro >= 0 ? "text-success" : "text-danger";
                        return `<span class="${className} fw-bold">${numberFormat(lucro, 2)} Kz</span>`;
                      },
                    },
                    {
                      title: "MARGEM",
                      data: null,
                      render: (value, type, row) => {
                        const margem = getProductMargin(row);
                        const className = margem >= 0 ? "text-success" : "text-danger";
                        return `<span class="${className} fw-bold">${numberFormat(margem, 2)}%</span>`;
                      },
                    },
                    { title: "STOCK", data: "quantidade", render: (value, type, row) => row.tipo === "Servico" || row.tipo === "Serviço" ? "---" : value },
                    { title: "EXPIRACAO", data: "data_expiracao", render: (value) => value || "---" },
                  ]}
                  data={data}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" backdrop="static" contentClassName="rounded-0">
        <form onSubmit={handleSave}>
          <Modal.Header closeButton className="py-2">
            <Modal.Title className="fs-6 fw-bold text-uppercase">{isEditing ? "Editar produto" : "Novo produto"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-2">
              <div className="col-md-4">
                <label className="small fw-bold text-muted">TIPO</label>
                <select className="form-select form-select-sm rounded-0" value={produtoData.tipo || "Produto"} onChange={(event) => setProdutoData({ ...produtoData, tipo: event.target.value })}>
                  <option value="Produto">Produto</option>
                  <option value="Serviço">Servico</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="small fw-bold text-muted">DESIGNACAO</label>
                <input type="text" className="form-control form-control-sm rounded-0" required value={produtoData.nome || ""} onChange={(event) => setProdutoData({ ...produtoData, nome: event.target.value })} />
              </div>
              <div className="col-md-4">
                <label className="small fw-bold text-muted">CODIGO DE BARRAS</label>
                <input type="text" className="form-control form-control-sm rounded-0" value={produtoData.barcode || ""} onChange={(event) => setProdutoData({ ...produtoData, barcode: event.target.value })} />
              </div>

              <div className="col-12">
                <label className="small fw-bold text-muted">DESCRICAO/NOTAS</label>
                <textarea rows="1" className="form-control form-control-sm rounded-0" value={produtoData.descricao || ""} onChange={(event) => setProdutoData({ ...produtoData, descricao: event.target.value })} />
              </div>

              <div className="col-md-6">
                <label className="small fw-bold text-danger">TAXES *</label>
                <Select styles={selectStyle} options={OPTIONS_TAXES} value={selectedTaxOption} onChange={(selected) => setProdutoData({ ...produtoData, taxes: selected ? selected.rawTax : {} })} />
              </div>

              <div className="col-md-6">
                <label className={`small fw-bold ${produtoData.preco_venda_atendimento ? "text-muted opacity-50" : "text-primary"}`}>
                  PRECO VENDA BASE {!produtoData.preco_venda_atendimento && "*"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control form-control-sm rounded-0"
                  required={!produtoData.preco_venda_atendimento}
                  disabled={produtoData.preco_venda_atendimento}
                  value={produtoData.preco_venda_atendimento ? "" : (produtoData.preco || "")}
                  onChange={(event) => setProdutoData({ ...produtoData, preco: event.target.value })}
                />
              </div>

              <div className="col-md-6">
                <label className="small fw-bold text-muted">UNIDADE</label>
                <select className="form-select form-select-sm rounded-0" value={produtoData.unidade || "un"} onChange={(event) => setProdutoData({ ...produtoData, unidade: event.target.value })}>
                  <option value="Nenhuma">Nenhuma</option>
                  <option value="un">un</option>
                  <option value="cx">cx</option>
                  <option value="pç">pc</option>
                  <option value="g">g</option>
                  <option value="kg">kg</option>
                  <option value="l">l</option>
                  <option value="ml">ml</option>
                  <option value="mg">mg</option>
                  <option value="m">m</option>
                  <option value="cm">cm</option>
                </select>
              </div>

              <div className="col-md-6">
                <label className="small fw-bold text-muted">QUANTIDADE STOCK</label>
                <input type="number" step="0.01" className="form-control form-control-sm rounded-0" value={produtoData.quantidade || ""} onChange={(event) => setProdutoData({ ...produtoData, quantidade: event.target.value })} />
              </div>

              <div className="col-12 my-2">
                <div className="form-check form-switch ps-5 py-2 border bg-light bg-opacity-50">
                  <input
                    className="form-check-input cursor-pointer"
                    type="checkbox"
                    id="precoVendaAtendimento"
                    checked={!!produtoData.preco_venda_atendimento}
                    onChange={(event) => setProdutoData({ ...produtoData, preco_venda_atendimento: event.target.checked, preco: event.target.checked ? "" : produtoData.preco })}
                  />
                  <label className="form-check-label small fw-bold text-dark cursor-pointer" htmlFor="precoVendaAtendimento">
                    Definir preco livremente no POS/faturacao
                  </label>
                </div>
              </div>

              {produtoData.tipo !== "Serviço" && (
                <>
                  <div className="col-md-4">
                    <label className="small fw-bold text-muted">PRECO COMPRA</label>
                    <input type="number" step="0.01" className="form-control form-control-sm rounded-0" value={produtoData.preco_compra || ""} onChange={(event) => setProdutoData({ ...produtoData, preco_compra: event.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <label className="small fw-bold text-muted">DATA PRODUCAO</label>
                    <input type="date" className="form-control form-control-sm rounded-0" value={produtoData.data_producao || ""} onChange={(event) => setProdutoData({ ...produtoData, data_producao: event.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <label className="small fw-bold text-danger">DATA EXPIRACAO</label>
                    <input type="date" className="form-control form-control-sm rounded-0" value={produtoData.data_expiracao || ""} onChange={(event) => setProdutoData({ ...produtoData, data_expiracao: event.target.value })} />
                  </div>
                </>
              )}

              <div className="col-12 mt-2">
                <div className="p-2 border bg-light d-flex justify-content-between align-items-center">
                  <span className="small text-muted fw-bold">RESUMO FINANCEIRO:</span>
                  <span className="fw-bold fs-6">
                    PRECO FINAL: <span className="text-primary">{produtoData.preco_venda_atendimento ? "Definido no POS" : `${numberFormat(precoComTaxa, 2)} Kz`}</span>
                    {!produtoData.preco_venda_atendimento && (
                      <>
                        {" | "}
                        LUCRO: <span className={(valorPrecoBase - (parseFloat(produtoData.preco_compra) || 0)) >= 0 ? "text-success" : "text-danger"}>
                          {numberFormat(valorPrecoBase - (parseFloat(produtoData.preco_compra) || 0), 2)} Kz
                        </span>
                        {" | "}
                        MARGEM: <span className={(valorPrecoBase - (parseFloat(produtoData.preco_compra) || 0)) >= 0 ? "text-success" : "text-danger"}>
                          {numberFormat(valorPrecoBase > 0 ? ((valorPrecoBase - (parseFloat(produtoData.preco_compra) || 0)) / valorPrecoBase) * 100 : 0, 2)}%
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light py-1">
            <button type="button" className="win10-btn win10-btn-outline px-4" onClick={() => setShowModal(false)}>Cancelar</button>
            <button type="submit" className="win10-btn win10-btn-primary px-4">Gravar</button>
          </Modal.Footer>
        </form>
      </Modal>
      <Footer />
    </div>
  );
};

export default Produtos;
